"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

function hasValue(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const QuotationPrint = forwardRef(function QuotationPrint({ quotation }, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [freshQuotation, setFreshQuotation] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: quotation?.quotation_no || "quotation",
  });

  const prepareFreshData = async () => {
    if (!quotation?.id) return;
    const full = (await rmcClient.getQuotation(quotation.id)).data;
    flushSync(() => {
      setFreshQuotation(full);
    });
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!quotation) return;
      await prepareFreshData();
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  useEffect(() => {
    setFreshQuotation(null);
  }, [quotation?.id]);

  if (!quotation) {
    return <div ref={contentRef} className="hidden" aria-hidden />;
  }

  const source = freshQuotation || quotation;
  const items = Array.isArray(source?.items) ? source.items.filter((it) => Number(it?.rate) > 0) : [];

  const companyName = pick(company?.name, company?.company_name) || "Company Name";
  const companyAddress = pick(company?.address, company?.address_line1);
  const companyPhone = pick(
    company?.contact_phone,
    company?.mobile_no,
    company?.mobile,
    company?.mobile_number,
    company?.phone,
    company?.contact_no
  );
  const companyGst = pick(company?.gst_number, company?.gstin);
  const companyEmail = pick(company?.email, company?.contact_email);
  const companyBlessing = pick(company?.blessing, company?.invoice_blessing);

  const partyObj = source?.party || {};
  const partyName = pick(partyObj?.name);
  const partyAddress = pick(partyObj?.address, partyObj?.billing_address, partyObj?.address_line1);
  const partyPhone = pick(
    partyObj?.contact_no,
    partyObj?.phone,
    partyObj?.mobile_no,
    partyObj?.mobile,
    partyObj?.mobile_number
  );
  const partyGst = pick(partyObj?.gst_number, partyObj?.gstin);

  const quotationNo = pick(source?.quotation_no, source?.id ? `QT-${source.id}` : "");
  const quotationDate = pick(source?.date);
  const validUntil = pick(source?.valid_until);
  const taxType = String(source?.tax_type ?? "").toUpperCase() === "GST" ? "GST" : "Non-GST";
  const gstRate = Number(source?.gst_rate || 0);
  const showGstRate = String(source?.tax_type ?? "").toUpperCase() === "GST";
  const emptyFillHeightPx = Math.max(220, 620 - items.length * 38);

  const leftInfoRows = [
    { label: "Quotation Number", value: quotationNo },
    { label: "Quotation Date", value: quotationDate },
    { label: "Valid Until", value: validUntil },
  ].filter((row) => hasValue(row.value));

  const rightInfoRows = [
    { label: "Tax Type", value: taxType },
    { label: "GST Rate", value: showGstRate ? `${gstRate.toFixed(2)}%` : "" },
    { label: "Status", value: source?.is_active ? "Active" : "Inactive" },
  ].filter((row) => hasValue(row.value));

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <section className="mx-auto flex h-[297mm] w-[210mm] flex-col border border-slate-700 p-2 text-[10px] leading-tight">
          {hasValue(companyBlessing) && (
            <div className="mb-2 text-center text-[10px] italic text-slate-700 print:break-inside-avoid">
              {companyBlessing}
            </div>
          )}

          <div className="border border-slate-600">
            <div className="border-b border-slate-600 px-2 py-1.5 text-center">
              <p className="text-[9px] font-semibold uppercase tracking-wide">Quotation</p>
              <p className="text-base font-bold leading-tight">{companyName}</p>
              {hasValue(companyAddress) && <p className="text-[10px] text-slate-700">{companyAddress}</p>}
              {(hasValue(companyPhone) || hasValue(companyEmail) || hasValue(companyGst)) && (
                <p className="text-[10px] text-slate-700">
                  {hasValue(companyPhone) && `Mobile: ${companyPhone}`}
                  {hasValue(companyPhone) && hasValue(companyEmail) && " | "}
                  {hasValue(companyEmail) && `Email: ${companyEmail}`}
                  {(hasValue(companyPhone) || hasValue(companyEmail)) && hasValue(companyGst) && " | "}
                  {hasValue(companyGst) && `GSTIN: ${companyGst}`}
                </p>
              )}
            </div>

            {(leftInfoRows.length > 0 || rightInfoRows.length > 0) && (
              <div className="grid grid-cols-2 border-b border-slate-600">
                <div className="border-r border-slate-600 px-2 py-2">
                  {leftInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-2 py-2">
                  {rightInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(hasValue(partyName) || hasValue(partyAddress) || hasValue(partyPhone) || hasValue(partyGst)) && (
              <div className="border-b border-slate-600 px-2 py-2">
                <p className="mb-1 font-semibold">Party Details</p>
                {hasValue(partyName) && (
                  <p>
                    <span className="font-semibold">Name</span>: {partyName}
                  </p>
                )}
                {(hasValue(partyPhone) || hasValue(partyGst)) && (
                  <p>
                    {hasValue(partyGst) && (
                      <>
                        <span className="font-semibold">GSTIN</span>: {partyGst}
                      </>
                    )}
                    {hasValue(partyGst) && hasValue(partyPhone) && " | "}
                    {hasValue(partyPhone) && (
                      <>
                        <span className="font-semibold">Mobile</span>: {partyPhone}
                      </>
                    )}
                  </p>
                )}
                {hasValue(partyAddress) && <p>{partyAddress}</p>}
              </div>
            )}
          </div>

          <div className="mt-2">
            <table className="w-full border-collapse border border-slate-600">
              <thead>
                <tr className="bg-slate-100">
                  <th className="w-10 border border-slate-600 px-2 py-1 text-center">Sr.</th>
                  <th className="border border-slate-600 px-2 py-1 text-left">Grade</th>
                  <th className="w-32 border border-slate-600 px-2 py-1 text-right">Rate (Rs / CUM)</th>
                </tr>
              </thead>
              <tbody>
                {items.map((line, idx) => (
                  <tr key={line?.grade?.id ?? idx}>
                    <td className="border border-slate-600 px-2 py-1 text-center align-top">{idx + 1}</td>
                    <td className="border border-slate-600 px-2 py-1 align-top">
                      {pick(line?.grade?.name, line?.grade_name, `Grade #${idx + 1}`)}
                    </td>
                    <td className="border border-slate-600 px-2 py-1 text-right align-top">
                      {fmtMoney(line?.rate) || "0.00"}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td className="border border-slate-600 px-2 py-3 text-center text-slate-600" colSpan={3}>
                      No quotation items available.
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="border border-slate-600 px-2 py-1" style={{ height: `${emptyFillHeightPx}px` }} />
                  <td className="border border-slate-600 px-2 py-1" />
                  <td className="border border-slate-600 px-2 py-1" />
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-2 pt-1">
            <div className="min-h-24 border border-slate-600 p-2 text-[10px]">
              <p className="font-semibold">Note</p>
              <p>This quotation is based on the currently discussed rates and is subject to final confirmation.</p>
            </div>
            <div className="flex min-h-24 items-end justify-end border border-slate-600 p-2 text-[10px]">
              <p className="font-semibold">For {companyName}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});

QuotationPrint.displayName = "QuotationPrint";

export default QuotationPrint;
